import { AIProviderBase } from '@type/provider';
import { debug } from '@utils/debug';

export class ChatStreamHandler {
  private decoder: TextDecoder;
  private provider: AIProviderBase;
  private aborted: boolean = false;
  private contentBuffer: string = '';
  private batchTimeout: NodeJS.Timeout | null = null;

  constructor(decoder: TextDecoder, provider: AIProviderBase) {
    this.decoder = decoder;
    this.provider = provider;
    
    debug.log('stream', `🔧 ChatStreamHandler created for provider: ${provider?.id || 'unknown'}`);
    
    if (!provider) {
      debug.error('stream', '❌ Provider is undefined in ChatStreamHandler constructor');
    }
  }

  async processStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    onContent: (content: string) => void
  ): Promise<void> {
    debug.log('stream', '🔄 Starting to process stream');
    
    if (!reader) {
      debug.error('stream', '❌ Reader is undefined in processStream');
      throw new Error('Reader is undefined');
    }
    
    if (!onContent) {
      debug.error('stream', '❌ Content callback is undefined in processStream');
      throw new Error('Content callback is undefined');
    }
    
    const flushBuffer = () => {
      if (this.contentBuffer) {
        onContent(this.contentBuffer);
        this.contentBuffer = '';
      }
    };

    try {
      while (!this.aborted) {
        const { done, value } = await reader.read();
        if (done) break;

        const content = this.parseChunk(value);
        this.contentBuffer += content;

        // Increase batch size for Apple Silicon
        const BATCH_TIMEOUT = navigator.userAgent.includes('Mac') ? 100 : 50; // Longer batching on Mac
        const performanceWithMemory = performance as Performance & {
          memory?: { usedJSHeapSize?: number };
        };

        // Add memory pressure detection
        if ((performanceWithMemory.memory?.usedJSHeapSize ?? 0) > 50 * 1024 * 1024) { // 50MB threshold
          // Force immediate flush on memory pressure
          flushBuffer();
        }

        if (!this.batchTimeout) {
          this.batchTimeout = setTimeout(() => {
            flushBuffer();
            this.batchTimeout = null;
          }, BATCH_TIMEOUT);
        }
      }
    } catch (error) {
      debug.error('stream', '❌ Stream processing error:', error);
      throw error;
    }
  }

  abort(): void {
    debug.log('stream', '🛑 Aborting stream processing');
    this.aborted = true;
    
    // Clear any pending timeout
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }
    
    // Flush remaining content
    if (this.contentBuffer) {
      // You might want to flush or clear this
      this.contentBuffer = '';
    }
  }

  private parseChunk(value: Uint8Array): string {
    const chunk = this.decoder.decode(value);
    debug.log('stream', `📦 Received chunk of size: ${chunk.length} bytes`);
    
    const lines = chunk.split('\n').filter(line => line.trim() !== '');
    debug.log('stream', `📝 Processing ${lines.length} lines from chunk`);

    let chunkContent = ''; // Local accumulator
    for (const line of lines) {
      if (this.aborted) {
        debug.log('stream', '🛑 Processing aborted during line processing');
        break;
      }
      
      if (line.startsWith('data: ')) {
        const data = line.slice(6);
        if (data === '[DONE]') {
          debug.log('stream', '✅ Received [DONE] signal');
          continue;
        }

        try {
          const result = JSON.parse(data);
          debug.log('stream', '🔍 Parsing streaming response');
          
          if (!this.provider) {
            debug.error('stream', '❌ Provider is undefined when parsing streaming response');
            continue;
          }
          
          const content = this.provider.parseStreamingResponse(result);
          if (content) {
            debug.log('stream', `📤 Sending content: "${content.substring(0, 20)}${content.length > 20 ? '...' : ''}"`);
            chunkContent += content; // Accumulate locally
          }
        } catch (e) {
          debug.error('stream', '❌ Failed to parse chunk:', e);
        }
      }
    }
    return chunkContent; // Return only this chunk's content
  }
}
